import { type HttpError, type UseQueryLightError } from '@atlaskit/teams-client';
import type { UFOExperience } from '@atlassian/ufo-experimental/experience';

import { is4xxError, isAuthError, isUseQueryLightError } from '../utils/error';
import { UnknownError } from '../utils/unknown-error';

import { isNetworkError } from './experience-tracker';

const ignoredErrors = ['SentryIgnoreError'];

const isIgnoredError = (error: Error): boolean => {
	const errorName = error?.constructor.name;
	if (!errorName) {
		return false;
	}

	return ignoredErrors.includes(errorName);
};

const createErrorMetadata = (error: Error | HttpError) => ({
	error: {
		name: error.name,
		message: error.message,
		stack: error.stack,
		traceId: (error as HttpError)?.traceId,
	},
});

export function failUFOExperience(
	experience: UFOExperience,
	caughtError: Error | UseQueryLightError | unknown,
	metadata = {},
): void {
	const error = caughtError instanceof Error ? caughtError : new UnknownError(caughtError);
	if (isAuthError(error)) {
		experience.abort({
			metadata: {
				...metadata,
				error: createErrorMetadata(error),
				reason: 'Abort due to user unauthenticated',
			},
		});
		return;
	}

	if (is4xxError(error)) {
		experience.abort({
			metadata: {
				...metadata,
				error: createErrorMetadata(error),
				reason: 'Abort due to ignored 4xx errors',
			},
		});
		return;
	}

	if (isIgnoredError(error)) {
		experience.abort({
			metadata: {
				...metadata,
				error: createErrorMetadata(error),
				reason: 'Abort due to an error in ignore list',
			},
		});
		return;
	}

	if (isNetworkError(error)) {
		experience.abort({
			metadata: {
				...metadata,
				error: createErrorMetadata(error),
			},
		});
		return;
	}

	if (isUseQueryLightError(caughtError)) {
		experience.failure({
			metadata: {
				...metadata,
				error: caughtError.message,
				extraInfo: caughtError.extraInfo,
				graphQLErrors: caughtError.graphQLErrors?.reduce<Record<string, Record<string, string>>>(
					(acc, error, index) => {
						acc[`graphqlError${index}`] = {
							message: error.message,
							category: error.category || '',
							path: error.path ? error.path.join('_') : '',
						};
						return acc;
					},
					{},
				),
			},
		});
	} else {
		experience.failure({
			metadata: {
				...metadata,
				error: createErrorMetadata(error),
			},
		});
	}
}
